import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Brand } from '@fametrue/shared';
import { formatEuro } from './palette';

/** Blocco navy con danno reputazionale e sanzione potenziale. */
export function DamageCard({ brand }: { brand: Brand }): ReactNode {
  const { t } = useTranslation();
  const d = brand.reputationalDamage;
  const fr = brand.fameRisk;

  return (
    <div
      style={{
        background:
          'linear-gradient(165deg, var(--b2-reale-a) 0%, var(--b2-notte-b) 50%, var(--b2-notte-a) 100%)',
        borderRadius: 10,
        padding: '16px 20px',
        marginBottom: 16,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 80% 0%, rgba(163,45,45,0.12) 0%, transparent 55%),radial-gradient(ellipse at 0% 100%, rgba(42,157,143,0.05) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />
      {/* La filigrana si nasconde sotto i 560 px: su telefono finisce
          esattamente sotto la cifra del danno e il numero non si legge. Il
          riferimento non la nasconde mai — è l'unico suo difetto che il
          committente ha chiesto di correggere invece di replicare (16/10,
          Level 2 punto 5). La classe serve solo a poterla spegnere da CSS. */}
      <div
        className="damage-watermark"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          fontFamily: 'var(--font-display)',
          fontSize: 55,
          fontWeight: 500,
          letterSpacing: '-1px',
          opacity: 0.8,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        <span style={{ color: 'rgba(255,255,255,0.9)' }}>Fame</span>
        <span style={{ color: '#C43030' }}>Risk</span>
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 600,
            }}
          >
            {t('decision.economicImpactYear1')}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              letterSpacing: '1.4px',
              textTransform: 'uppercase',
              fontWeight: 700,
              padding: '4px 9px',
              background: 'rgba(255,90,77,0.12)',
              border: '1px solid rgba(255,90,77,0.35)',
              borderRadius: 3,
              color: '#FF8B7A',
            }}
          >
            {t('decision.riskWithLevel', { level: t(`riskLevel.${fr.level}`) })}
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.45)',
              marginBottom: 4,
            }}
          >
            {t('decision.estimatedDamage')}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 28,
              fontWeight: 400,
              letterSpacing: '-0.8px',
              color: '#FF8B7A',
            }}
          >
            {formatEuro(d.min)}{' '}
            <span style={{ color: 'rgba(255,139,122,0.4)', margin: '0 2px' }}>–</span>{' '}
            {formatEuro(d.max)}
          </div>
        </div>

        <div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.45)',
              marginBottom: 4,
            }}
          >
            {t('decision.potentialSanction')}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 16,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.85)',
            }}
          >
            {formatEuro(fr.sanctionMin)} <span style={{ color: 'rgba(255,255,255,0.3)' }}>–</span>{' '}
            {formatEuro(fr.sanctionMax)}
          </div>
        </div>
      </div>
    </div>
  );
}
